/**
 * Rich Text Element Player
 */

import * as React from 'react';
import { RichTextViewer, cn, isTiptapDocument } from '@ai-content-hub/content-kit';
import { useElementUITranslations } from '../i18n/elementUI';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import type { RichTextElementData } from './schema';
import { normalizeImportedTiptapContent } from './importNormalize';

export interface RichTextPlayerProps extends ElementPlayerProps<RichTextElementData> {}

function isEmptyContent(content: RichTextElementData['content']): boolean {
    if (content == null) return true;
    if (typeof content === 'string') return content.trim() === '';
    if (
        typeof content === 'object' &&
        'markdown' in content &&
        typeof (content as { markdown: string }).markdown === 'string'
    )
        return (content as { markdown: string }).markdown.trim() === '';
    if (typeof content === 'object' && Array.isArray((content as { blocks?: unknown[] }).blocks))
        return ((content as { blocks: unknown[] }).blocks?.length ?? 0) === 0;
    if (isTiptapDocument(content)) {
        const nodes = content.content ?? [];
        if (nodes.length === 0) return true;
        // WHY: GFM pipe tables become tip-tap `table` nodes; text lives under
        // tableRow → tableCell → paragraph. A shallow text-only check wrongly
        // treated table-only import passages as empty ("No content").
        return !tiptapNodesHaveVisibleContent(nodes);
    }
    return true;
}

function tiptapNodesHaveVisibleContent(nodes: Array<{ type?: string; content?: unknown[] }>): boolean {
    for (const node of nodes) {
        const type = node.type ?? '';
        if (type === 'image' || type === 'audio' || type === 'embed' || type === 'table') {
            return true;
        }
        if (type === 'text') {
            if ((node as { text?: string }).text?.trim()) {
                return true;
            }
            continue;
        }
        const inner = (node.content ?? []) as Array<{ type?: string; content?: unknown[] }>;
        if (inner.length > 0 && tiptapNodesHaveVisibleContent(inner)) {
            return true;
        }
    }
    return false;
}

export const RichTextPlayer: React.FC<RichTextPlayerProps> = ({ data, className }) => {
    const et = useElementUITranslations();
    if (!data) {
        return (
            <div className={cn('text-muted-foreground italic', className)}>{et.noRichTextData}</div>
        );
    }

    const { content } = data;

    const viewerContent = React.useMemo(() => {
        const normalized = normalizeImportedTiptapContent(content);
        return normalized ?? content;
    }, [content]);

    if (isEmptyContent(content)) {
        return <div className={cn('text-muted-foreground italic', className)}>{et.noContent}</div>;
    }

    return (
        <div className={cn('rich-text-element break-words', className)}>
            <RichTextViewer data={viewerContent} className={className} />
        </div>
    );
};

export default RichTextPlayer;
